// app/src/lib/scale/plan.js — trial + paid plan gating (US-5.1, US-5.2).
//
// Plan state is the AUTHORITATIVE record of whether the app should be writable.
// On every mutation, callers should pass through `requireWritable()` which throws
// when the trial has expired AND the user is not on an `active` plan.
//
// Defense-in-depth: the JWT issued by Cognito carries a `custom:planTier` claim
// that the API Gateway authorizer reads server-side; this client-side gate is a
// UX layer, not the security boundary.

import { db } from '../db.js';
import { currentClaims } from '../auth/cognitoClient.js';
import { currentAccount } from './account.js';

const TRIAL_DAYS = 30;                 // 30-day free trial (COGS), starts on first scan
const TRIAL_PAGES_PER_WEEK = 20;
// Mirrors the server dunning window (billingSweep: 3 attempts, retried daily).
const GRACE_DAYS_AFTER_FAILED_PAYMENT = 3;

/** @typedef {'trial'|'active'|'grace'|'lapsed'|'cancelled'} PlanTier */
/** @typedef {{ userId:string, planTier:PlanTier, trialStartedAt:number, trialExpiresAt:number, currentPeriodEnd?:number, lastPaymentStatus?:string, lastPaymentAt?:number }} PlanState */

export async function ensurePlan() {
  const claims = await currentClaims();
  if (!claims) throw new Error('plan: not signed in');
  const userId = claims.sub;
  const existing = await db.planState.get(userId);
  if (existing) return existing;
  // No free plan: a new account is in 'trial' billing state but the 4-week clock
  // does NOT start until the first page is scanned (see recordPages). Until then
  // trialStartedAt is null and the app stays writable so they can get going.
  const fresh = {
    userId,
    planTier: 'trial',          // billing state (trial/active/grace/lapsed/cancelled)
    tier: null,                 // product tier (basic/pro/payg) — set when they subscribe
    trialStartedAt: null,
    trialExpiresAt: null,
  };
  await db.planState.put(fresh);
  return fresh;
}

/** Read the current plan, applying server-source-of-truth if the JWT has a custom claim. */
export async function getPlan() {
  const claims = await currentClaims();
  if (!claims) return null;
  let state = await ensurePlan();
  // The JWT custom claims are authoritative; reconcile billing state, product
  // tier, and the trial's selected tier (drives trial feature entitlements).
  const claimTier = claims['custom:planTier'];
  const claimProduct = claims['custom:tier'];
  const claimIntended = claims['custom:intendedTier'];
  let dirty = false;
  if (typeof claimTier === 'string' && claimTier !== state.planTier) {
    state = { ...state, planTier: /** @type {PlanTier} */ (claimTier) }; dirty = true;
  }
  if (typeof claimProduct === 'string' && TIERS[claimProduct] && claimProduct !== state.tier) {
    state = { ...state, tier: claimProduct }; dirty = true;
  }
  if (typeof claimIntended === 'string' && TIERS[claimIntended] && claimIntended !== state.intendedTier) {
    state = { ...state, intendedTier: claimIntended }; dirty = true;
  }
  // Paid-through boundary — drives "cancelled/grace but paid until period end".
  const claimPeriodEnd = Number(claims['custom:periodEnd']);
  if (Number.isFinite(claimPeriodEnd) && claimPeriodEnd > 0 && claimPeriodEnd !== state.currentPeriodEnd) {
    state = { ...state, currentPeriodEnd: claimPeriodEnd }; dirty = true;
  }
  if (dirty) await db.planState.put(state);
  return state;
}

// ─── Product tiers (entitlements) ────────────────────────────────────────────
// Distinct from billing state (planTier). Pages are the metered unit (OCR cost).
/** @typedef {'basic'|'pro'|'payg'} ProductTier */
// No free tier — the only free access is the 4-week trial. `features` gate
// non-COGS surfaces: cashflow needs a linked bank; delivery = DoorDash/UberEats/
// Grubhub monthly analysis. annualOnlyCents is the annual price in states where
// we only offer annual billing.
export const TIERS = {
  basic: {
    label: 'Basic', pagesPerWeek: 15, maxInstitutions: 0,
    monthlyCents: 500, annualCents: 5000, annualOnlyCents: 6000,
    features: { cashflow: false, delivery: false },
  },
  pro: {
    label: 'Pro', pagesPerWeek: 50, maxInstitutions: 2,
    monthlyCents: 900, annualCents: 9900, annualOnlyCents: 9900,
    features: { cashflow: true, delivery: true },
  },
  // À la carte: $4 base (10 pages/week), +$1/institution, +$1 per extra 20
  // pages/week, +$2 delivery analysis. Pure monthly; no annual.
  payg: {
    label: 'À la carte', pagesPerWeek: null, maxInstitutions: null,
    monthlyCents: 400, metered: true,
    features: { cashflow: true, delivery: true }, // available as paid add-ons
  },
};

const PAYG_BASE_CENTS = 400;            // $4/mo includes 10 pages/week
const PAYG_BASE_PAGES = 10;
const PAYG_PER_20_PAGES_CENTS = 100;    // $1/mo per additional 20 pages/week
const PAYG_PER_INSTITUTION_CENTS = 100; // $1/mo per connected financial institution
const PAYG_DELIVERY_CENTS = 200;        // $2/mo delivery analysis add-on
export const PAYG_PER_USER_CENTS = 100; // $1/mo per additional read-only user
export const TEAM_ADDON_CENTS = 200;    // $2/mo flat (basic/pro): up to 3 viewers

/** Monthly price (cents) for an À la carte configuration. */
export function paygMonthlyCents({ pagesPerWeek = 0, institutions = 0, delivery = false, additionalUsers = 0 } = {}) {
  const extraPages = Math.max(0, pagesPerWeek - PAYG_BASE_PAGES);
  const extraBlocks = Math.ceil(extraPages / 20);
  return PAYG_BASE_CENTS
    + extraBlocks * PAYG_PER_20_PAGES_CENTS
    + Math.max(0, institutions) * PAYG_PER_INSTITUTION_CENTS
    + (delivery ? PAYG_DELIVERY_CENTS : 0)
    + Math.max(0, Math.min(3, additionalUsers)) * PAYG_PER_USER_CENTS;
}

/** Subscription price (cents) for a fixed tier + cycle, location-aware. */
export function priceCents(tier, cycle = 'monthly', { annualOnly = false } = {}) {
  const t = TIERS[tier];
  if (!t || t.metered) return null;
  if (cycle === 'annual') return annualOnly ? (t.annualOnlyCents ?? t.annualCents) : t.annualCents;
  return t.monthlyCents;
}

const TRIAL_ENT = { tier: 'trial', metered: false, pagesPerWeek: TRIAL_PAGES_PER_WEEK, maxInstitutions: 0, features: { cashflow: false, delivery: false } };

/** Max additional read-only team users the plan allows (mirrors quota.mjs). */
export function maxAdditionalUsers(state) {
  if (!state || state.planTier === 'trial' || !state.tier) return 0;
  if (state.tier === 'payg') return Math.max(0, Math.min(3, state?.additionalUsers || 0));
  return state?.teamAddOn ? 3 : 0;
}

/** Feature flags for a selected product tier (independent of billing state).
 *  During a trial, the tier picked at card-on-file signup (intendedTier) counts
 *  — a Pro trial should feel like Pro. */
function featuresFor(state) {
  const tier = state && (state.tier || state.intendedTier);
  if (tier === 'payg') return { cashflow: (state?.paygInstitutions ?? 0) > 0, delivery: state?.paygDelivery === true };
  const t = tier && TIERS[tier];
  return t ? t.features : { cashflow: false, delivery: false };
}

/**
 * Resolve the effective entitlements for a plan state.
 *
 * During the trial (planTier='trial') the user runs on TRIAL page limits, but
 * gets the FEATURES of whatever product tier they've selected — a Pro trial
 * should feel like Pro (delivery, cashflow), not a stripped-down free tier.
 * With no tier chosen yet, trial = COGS only.
 */
/** Deploy-configurable Pro institution cap (from config.js proMaxInstitutions);
 *  defaults to 2 to match the server. Read from the runtime config global so it's
 *  available without an import cycle and regardless of boot ordering. */
function proMaxInstitutions() {
  const n = Number(globalThis.__KANJO_CONFIG__ && globalThis.__KANJO_CONFIG__.proMaxInstitutions);
  return Number.isFinite(n) && n > 0 ? n : 2;
}

export function entitlements(state) {
  if (!state || state.planTier === 'trial') {
    // Trial PAGE limits, but the selected tier's features + institution cap
    // (mirrors quota.checkInstitutionCap's trial rule).
    const sel = state && (state.tier || state.intendedTier);
    const selCfg = sel && sel !== 'payg' ? TIERS[sel] : null;
    const maxInstitutions = selCfg ? (sel === 'pro' ? proMaxInstitutions() : selCfg.maxInstitutions) : 0;
    return { tier: 'trial', metered: false, pagesPerWeek: TRIAL_PAGES_PER_WEEK, maxInstitutions, features: featuresFor(state) };
  }
  const tier = state.tier;
  if (tier === 'payg') {
    return {
      tier,
      metered: true,
      pagesPerWeek: state?.paygPagesPerWeek ?? PAYG_BASE_PAGES,
      maxInstitutions: state?.paygInstitutions ?? Infinity,
      features: { cashflow: (state?.paygInstitutions ?? 0) > 0, delivery: state?.paygDelivery === true },
    };
  }
  const t = TIERS[tier];
  if (!t) return { ...TRIAL_ENT };   // active but no tier chosen yet → COGS only
  // Pro's connected-institution cap is deploy-configurable (the sandbox stack
  // allows more for testing; production is 2). Mirrors the server's
  // PRO_MAX_INSTITUTIONS in kanjo-shared/quota.mjs.
  const maxInstitutions = tier === 'pro' ? proMaxInstitutions() : t.maxInstitutions;
  return { tier, metered: false, pagesPerWeek: t.pagesPerWeek, maxInstitutions, features: t.features };
}

// ─── Weekly page metering ────────────────────────────────────────────────────
/** ISO-8601 week key, e.g. "2026-W21". Quotas reset on the ISO week boundary. */
export function isoWeekKey(d = new Date()) {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = (date.getUTCDay() + 6) % 7;     // Mon=0..Sun=6
  date.setUTCDate(date.getUTCDate() - dayNum + 3); // nearest Thursday
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(
    ((date - firstThursday) / 86_400_000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7,
  );
  return `${date.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}

/** Pages consumed in the current ISO week (0 if the stored week is stale). */
export async function pagesUsedThisWeek() {
  const p = await getPlan();
  if (!p) return 0;
  return p.usageWeek === isoWeekKey() ? (p.pagesUsed || 0) : 0;
}

/** Record `n` scanned pages against this week's tally. Returns the new total. */
export async function recordPages(n) {
  const p = await ensurePlan();
  const wk = isoWeekKey();
  const base = p.usageWeek === wk ? (p.pagesUsed || 0) : 0;
  const pagesUsed = base + Math.max(0, n || 0);
  const patch = { ...p, usageWeek: wk, pagesUsed };
  // First page scanned starts the 4-week trial clock (only while in trial state).
  if ((n || 0) > 0 && p.planTier === 'trial' && !p.trialStartedAt) {
    const now = Date.now();
    patch.trialStartedAt = now;
    patch.trialExpiresAt = now + TRIAL_DAYS * 86_400_000;
    // Funnel: the first scan is first-value / activation. This block runs exactly
    // once per user (guarded by !trialStartedAt), so the event is naturally deduped.
    try { globalThis.kanjo?.track?.('activation', { via: 'first_scan' }); } catch { /* ignore */ }
  }
  await db.planState.put(patch);
  return pagesUsed;
}

/** Can the user scan `pages` more this week? PAYG always allows (billed for use). */
export async function canScan(pages = 1) {
  const p = await getPlan();
  const ent = entitlements(p);
  const used = await pagesUsedThisWeek();
  if (ent.metered) return { ok: true, metered: true, used };
  const limit = ent.pagesPerWeek ?? 0;
  return { ok: used + pages <= limit, used, limit, remaining: Math.max(0, limit - used) };
}

/** Can the user connect another financial institution (Plaid Item)? */
export async function canConnectInstitution() {
  const p = await getPlan();
  if (!p) return { ok: false, count: 0, max: 0 };
  const ent = entitlements(p);
  const count = await db.plaidItems.where('userId').equals(p.userId).count();
  if (ent.metered) return { ok: true, metered: true, count };
  return { ok: count < ent.maxInstitutions, count, max: ent.maxInstitutions };
}

/** Status summary for the settings panel. */
export async function summary() {
  const p = await getPlan();
  if (!p) return null;
  const now = Date.now();
  const trialStarted = !!p.trialStartedAt;
  const daysLeftTrial = !trialStarted ? TRIAL_DAYS : Math.max(0, Math.ceil((p.trialExpiresAt - now) / 86_400_000));
  return {
    planTier: p.planTier,
    trialStarted,
    daysLeftTrial,
    currentPeriodEnd: p.currentPeriodEnd || null,
    lastPaymentStatus: p.lastPaymentStatus || null,
    writable: await isWritable(p),
  };
}

/**
 * Returns true if the user can perform mutations.
 *   - trial: writable until trialExpiresAt.
 *   - active: writable.
 *   - grace: writable for GRACE_DAYS_AFTER_FAILED_PAYMENT days after lastPaymentAt.
 *   - lapsed / cancelled: read-only.
 *
 * The JWT custom claim is authoritative when set; this is the UX-layer check.
 */
export async function isWritable(state) {
  // Team accounts: read-only viewers can never mutate (fail-closed belt-and-
  // suspenders behind the UI gates). Owners fall through to the plan-tier check.
  try { const a = await currentAccount(); if (a && a.role === 'viewer') return false; } catch { /* default to plan check */ }
  const p = state || (await getPlan());
  if (!p) return false;
  const now = Date.now();
  switch (p.planTier) {
    case 'active': return true;
    case 'pending': return true; // first charge in flight — they just subscribed
    case 'trial':  return !p.trialStartedAt || p.trialExpiresAt > now; // pre-trial + during-trial writable
    case 'grace':  return !!p.lastPaymentAt && (p.lastPaymentAt + GRACE_DAYS_AFTER_FAILED_PAYMENT * 86_400_000) > now;
    case 'cancelled': return !!p.currentPeriodEnd && p.currentPeriodEnd > now; // paid-through until period end
    case 'lapsed':
    default:       return false;
  }
}

/** Throws with code='READ_ONLY' if the user cannot mutate. Callers wrap mutations. */
export async function requireWritable() {
  const ok = await isWritable();
  if (!ok) {
    const err = new Error('Account is read-only. Subscribe to continue.');
    err.code = 'READ_ONLY';
    throw err;
  }
}
